"use client";

import React, { useState } from "react";
import { Box, Database, List, Trash2, Type, X } from "lucide-react";
import type {
  InfoPort,
  InformationFormat,
  PortDirection,
  DbSchema,
} from "./types";
import { emptyPort, inputCls, labelCls, PRIMITIVE_TYPES } from "./constants";
import { NotesForm } from "./NotesForm";

// ─── Port Edit Form ────────────────────────────────────────────────────────────

function PortEditForm({
  port,
  onChange,
  onClose,
  accentColor,
  dbSchema,
}: {
  port: InfoPort;
  onChange: (p: InfoPort) => void;
  onClose: () => void;
  accentColor: string;
  dbSchema: DbSchema;
}) {
  const set = <K extends keyof InfoPort>(key: K, val: InfoPort[K]) =>
    onChange({ ...port, [key]: val });

  return (
    <div className="dark:bg-white/3 space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-white/10">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Port
        </span>
        <button onClick={onClose} className="text-gray-300 hover:text-gray-500">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Direction toggle */}
      <div>
        <label className={labelCls}>Richtung</label>
        <div className="flex gap-1.5">
          {(["input", "output"] as PortDirection[]).map((dir) => (
            <button
              key={dir}
              onClick={() => set("direction", dir)}
              className={`flex flex-1 items-center justify-center gap-1 rounded-xl border py-2 text-xs font-medium transition ${
                port.direction === dir
                  ? `border-${accentColor}-500/40 bg-${accentColor}-500/10 text-${accentColor}-400`
                  : "border-gray-200 text-gray-500 dark:border-white/10"
              }`}
            >
              {dir === "input" ? "↳ Eingang" : "Ausgang ↦"}
            </button>
          ))}
        </div>
      </div>

      {/* Name */}
      <div>
        <label className={labelCls}>Name</label>
        <input
          autoFocus
          className={inputCls}
          placeholder="z.B. userId"
          value={port.name}
          onChange={(e) => set("name", e.target.value)}
        />
      </div>

      {/* Format */}
      <div>
        <label className={labelCls}>Format</label>
        <div className="flex gap-1.5">
          {(["primitive", "list", "object"] as InformationFormat[]).map((f) => {
            const icons = { primitive: Type, list: List, object: Box };
            const FIcon = icons[f];
            return (
              <button
                key={f}
                onClick={() => set("format", f)}
                className={`flex flex-1 items-center justify-center gap-1 rounded-xl border py-2 text-xs font-medium transition ${
                  port.format === f
                    ? `border-${accentColor}-500/40 bg-${accentColor}-500/10 text-${accentColor}-400`
                    : "border-gray-200 text-gray-500 dark:border-white/10"
                }`}
              >
                <FIcon className="h-3.5 w-3.5" />
                {f === "primitive" ? "Wert" : f === "list" ? "Liste" : "Objekt"}
              </button>
            );
          })}
        </div>
      </div>

      {/* Primitive type picker */}
      {port.format === "primitive" && (
        <div>
          <label className={labelCls}>Datentyp</label>
          <div className="flex flex-wrap gap-1">
            {PRIMITIVE_TYPES.map((pt) => (
              <button
                key={pt}
                onClick={() => set("primitiveType", pt)}
                className={`rounded-full border px-2.5 py-1 font-mono text-[11px] transition ${
                  port.primitiveType === pt
                    ? `border-${accentColor}-500/40 bg-${accentColor}-500/10 text-${accentColor}-400`
                    : "border-gray-200 text-gray-500 dark:border-white/10"
                }`}
              >
                {pt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Required checkbox */}
      <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-500">
        <input
          type="checkbox"
          checked={port.required}
          onChange={(e) => set("required", e.target.checked)}
          className="h-3.5 w-3.5"
        />
        Pflichtfeld
      </label>

      {/* Description */}
      <div>
        <label className={labelCls}>
          Beschreibung ({port.direction === "input" ? "Eingang" : "Ausgang"})
        </label>
        <input
          className={inputCls}
          placeholder="Kurze Erklärung…"
          value={port.description ?? ""}
          onChange={(e) => set("description", e.target.value)}
        />
      </div>

      <NotesForm notes={port.notes} onChange={(n) => set("notes", n)} />

      {/* DB field – only relevant for output ports */}
      {port.direction === "output" && (
        <div>
          <label className={labelCls}>DB Speicherort</label>
          {dbSchema.tables.length === 0 ? (
            <p className="text-xs text-gray-400">
              Noch keine Tabellen im DB-Schema definiert.
            </p>
          ) : (
            <div className="flex gap-2">
              <select
                className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none dark:border-white/10 dark:bg-zinc-900 dark:text-gray-300"
                value={port.dbField?.tableId ?? ""}
                onChange={(e) => {
                  const tableId = e.target.value;
                  set(
                    "dbField",
                    tableId ? { tableId, columnId: "" } : undefined,
                  );
                }}
              >
                <option value="">— Kein Feld —</option>
                {dbSchema.tables.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || "(Kein Name)"}
                  </option>
                ))}
              </select>
              {port.dbField?.tableId && (
                <select
                  className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none dark:border-white/10 dark:bg-zinc-900 dark:text-gray-300"
                  value={port.dbField?.columnId ?? ""}
                  onChange={(e) =>
                    set("dbField", {
                      tableId: port.dbField!.tableId,
                      columnId: e.target.value,
                    })
                  }
                >
                  <option value="">— Spalte —</option>
                  {(
                    dbSchema.tables.find((t) => t.id === port.dbField?.tableId)
                      ?.columns ?? []
                  ).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || "(Kein Name)"}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Ports Section ─────────────────────────────────────────────────────────────

export function PortsSection({
  label,
  ports,
  onChange,
  accentColor,
  dbSchema,
}: {
  label: string;
  ports: InfoPort[];
  onChange: (ports: InfoPort[]) => void;
  accentColor: string;
  dbSchema: DbSchema;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const addPort = (direction: PortDirection = "input") => {
    const p = emptyPort(direction);
    onChange([...ports, p]);
    setEditingId(p.id);
  };

  const updatePort = (updated: InfoPort) => {
    onChange(ports.map((p) => (p.id === updated.id ? updated : p)));
  };

  const removePort = (id: string) => {
    onChange(ports.filter((p) => p.id !== id));
    if (editingId === id) setEditingId(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className={labelCls}>{label}</label>
        <div className="flex gap-2">
          <button
            onClick={() => addPort("input")}
            className="text-[11px] text-gray-400 hover:text-gray-600"
          >
            + Eingang
          </button>
          <button
            onClick={() => addPort("output")}
            className="text-[11px] text-gray-400 hover:text-gray-600"
          >
            + Ausgang
          </button>
        </div>
      </div>
      {ports.length === 0 && (
        <p className="text-xs text-gray-400">Noch keine Ports.</p>
      )}
      {ports.map((port) =>
        editingId === port.id ? (
          <PortEditForm
            key={port.id}
            port={port}
            onChange={updatePort}
            onClose={() => setEditingId(null)}
            accentColor={accentColor}
            dbSchema={dbSchema}
          />
        ) : (
          <div
            key={port.id}
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-zinc-900"
          >
            <button
              className="flex-1 text-left"
              onClick={() => setEditingId(port.id)}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={`rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                    port.direction === "input"
                      ? "bg-sky-500/10 text-sky-400"
                      : "bg-emerald-500/10 text-emerald-400"
                  }`}
                >
                  {port.direction === "input" ? "in" : "out"}
                </span>
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {port.name || (
                    <span className="italic text-gray-400">kein Name</span>
                  )}
                </p>
              </div>
              <p className="font-mono text-[10px] text-gray-400">
                {port.format === "primitive"
                  ? (port.primitiveType ?? "string")
                  : port.format}
                {!port.required && " · optional"}
              </p>
              {port.dbField?.columnId &&
                (() => {
                  const tbl = dbSchema.tables.find(
                    (t) => t.id === port.dbField!.tableId,
                  );
                  const col = tbl?.columns.find(
                    (c) => c.id === port.dbField!.columnId,
                  );
                  return tbl && col ? (
                    <p className="flex items-center gap-1 font-mono text-[10px] text-emerald-500">
                      <Database className="h-2.5 w-2.5" />
                      {tbl.name}.{col.name}
                    </p>
                  ) : null;
                })()}
            </button>
            <button
              onClick={() => removePort(port.id)}
              className="text-gray-300 hover:text-red-400"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ),
      )}
    </div>
  );
}
